<script lang="ts">
  // Field-team attention (scoped citizens only): blocked chains plus cards
  // sitting in Doing untouched for 3+ days. Read-only — a field viewer nudges
  // people offline; nothing here mutates anyone's board.
  import { createQuery } from '@tanstack/svelte-query';
  import { fieldApi } from '$lib/api/field';
  import { t } from '$lib/i18n';
  import type { TeamBoardCard } from '$lib/types';

  const STUCK_DOING_DAYS = 3;

  const query = createQuery(() => ({ queryKey: ['field-board'], queryFn: () => fieldApi.board() }));
  const cards = $derived(query.data?.cards ?? []);
  const cardById = $derived.by(() => {
    const map = new Map<number, TeamBoardCard>();
    for (const c of cards) map.set(c.id, c);
    return map;
  });

  function ageDays(updatedAt: string): number {
    return Math.max(0, Math.floor((Date.now() - new Date(updatedAt).getTime()) / 86400000));
  }

  const blocked = $derived(cards.filter((c) => c.col === 'blocked').sort((a, b) => ageDays(b.updated_at) - ageDays(a.updated_at)));
  const stale = $derived(
    cards
      .filter((c) => c.col === 'doing' && ageDays(c.updated_at) >= STUCK_DOING_DAYS)
      .sort((a, b) => ageDays(b.updated_at) - ageDays(a.updated_at))
  );
  const total = $derived(blocked.length + stale.length);

  type ChainRow = { card: TeamBoardCard; depth: number };
  function chainRows(root: TeamBoardCard): ChainRow[] {
    const rows: ChainRow[] = [{ card: root, depth: 0 }];
    const seen = new Set<number>([root.id]);
    const walk = (card: TeamBoardCard, depth: number) => {
      for (const helper of card.blockers ?? []) {
        const child = cardById.get(helper.card_id);
        if (!child || seen.has(child.id)) continue;
        seen.add(child.id);
        rows.push({ card: child, depth: depth + 1 });
        walk(child, depth + 1);
      }
    };
    walk(root, 0);
    return rows;
  }
</script>

<div class="tym-page">
  <h1 class="font-heading text-[16.5px] font-bold leading-[1.2] tracking-tight text-navy">{t('teamBoard.attentionTitle')}</h1>
  <p class="mb-3 mt-1 text-[13px] leading-[1.5] text-dark-gray">{t('fieldAttention.subtitle')}</p>

  {#if query.isLoading}
    <div class="tym-loading-state">{t('common.loading')}</div>
  {:else if query.isError}
    <div class="tym-error-state">{t('fieldAttention.error')}</div>
  {:else if total === 0}
    <div class="tym-empty-state">{t('teamBoard.attentionEmpty')}</div>
  {:else}
    {#if blocked.length}
      <h2 class="font-heading text-[11px] font-bold uppercase tracking-[.08em] text-navy">{t('teamBoard.attentionBlocked', { n: blocked.length })}</h2>
      <ul class="mb-5 mt-2 space-y-3">
        {#each blocked as root (root.id)}
          <li class="rounded-[10px] border border-toyama-cloudy-gray bg-surface-2 p-3">
            {#each chainRows(root) as row (row.card.id)}
              <div class="py-0.5 text-[13px] leading-[1.4]" style="margin-left:{row.depth * 18}px">
                {#if row.depth > 0}<span aria-hidden="true" class="mr-1 text-dark-gray">↳</span>{/if}<span class="font-semibold text-navy">{row.card.user_name}</span><span class="text-dark-gray"> — {row.card.title}</span>
                <span class="ml-1 rounded-full bg-surface px-1.5 py-px text-[10.5px] tabular-nums text-dark-gray">{t('teamBoard.ageDays', { n: ageDays(row.card.updated_at) })} · {row.card.col}</span>
                {#if row.depth === 0}
                  {#if row.card.blocked_reason}<p class="mt-0.5 text-[12px] text-red-700">{t('board.blockedReason', { reason: row.card.blocked_reason })}</p>{/if}
                  {#if row.card.blocked_external_name}<p class="mt-0.5 text-[12px] text-dark-gray">{t('board.blockedExternal', { name: row.card.blocked_external_name })}</p>{/if}
                {/if}
              </div>
            {/each}
          </li>
        {/each}
      </ul>
    {/if}
    {#if stale.length}
      <h2 class="font-heading text-[11px] font-bold uppercase tracking-[.08em] text-navy">{t('teamBoard.attentionStale', { n: stale.length })}</h2>
      <ul class="mt-2 space-y-2">
        {#each stale as card (card.id)}
          <li class="flex flex-wrap items-baseline gap-x-2 rounded-[10px] border border-toyama-cloudy-gray bg-surface-2 px-3 py-2 text-[13px] leading-[1.4]">
            <span class="font-semibold text-navy">{card.user_name}</span>
            <span class="min-w-0 flex-1 break-words text-dark-gray">{card.title}</span>
            <span class="rounded-full bg-surface px-1.5 py-px text-[10.5px] tabular-nums text-dark-gray">{t('teamBoard.attentionStaleAge', { age: t('teamBoard.ageDays', { n: ageDays(card.updated_at) }) })}</span>
          </li>
        {/each}
      </ul>
    {/if}
  {/if}
</div>
